/**
 * Reels Project Service
 * API integration for Motion Lab projects
 */

import { authHeaders } from '../../shared/utils/authHelpers';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEBUG = false;

const logRequest = (method, url, headers, body) => {
  if (!DEBUG) return;
  console.group(`📤 [ReelsProjectService] ${method} ${url}`);
  console.log('Headers:', headers);
  if (body) console.log('Body:', body);
  console.groupEnd();
};

const logResponse = (method, url, status, data) => {
  if (!DEBUG) return;
  const icon = status >= 200 && status < 300 ? '✅' : '❌';
  console.group(`${icon} [ReelsProjectService] ${method} ${url} - ${status}`);
  console.log('Response:', data);
  console.groupEnd();
};

const getAuthHeaders = () => ({
  ...authHeaders(),
  'Content-Type': 'application/json'
});

const normalizeList = (data, page, limit) => ({
  projects: data?.projects || data?.data || [],
  pagination: data?.pagination || {
    page,
    limit,
    total: data?.projects?.length || data?.data?.length || 0,
    totalPages: 1
  }
});

const unwrapProject = (data) => data?.data || data;

const ReelsProjectService = {
  async listProjects({ page = 1, limit = 20, search = '', status = 'draft', sort = 'modifiedAt', order = 'desc' } = {}) {
    const params = new URLSearchParams({ page, limit, sort, order });
    if (status) params.append('status', status);
    if (search) params.append('search', search);

    const url = `${API_BASE}/api/v1/reels/projects?${params}`;
    const headers = getAuthHeaders();

    logRequest('GET', url, headers, null);

    const response = await fetch(url, { headers });
    const data = await response.json().catch(() => ({}));

    logResponse('GET', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch motion projects');
    }

    return normalizeList(data, page, limit);
  },

  async getProject(id) {
    const url = `${API_BASE}/api/v1/reels/projects/${id}`;
    const headers = getAuthHeaders();

    logRequest('GET', url, headers, null);

    const response = await fetch(url, { headers });
    const data = await response.json().catch(() => ({}));

    logResponse('GET', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch motion project');
    }

    return unwrapProject(data);
  },

  async createProject({ name, settings }) {
    const url = `${API_BASE}/api/v1/reels/projects`;
    const headers = getAuthHeaders();
    const body = { name, settings };

    logRequest('POST', url, headers, body);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));

    logResponse('POST', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create motion project');
    }

    return unwrapProject(data);
  },

  async updateProject(id, project) {
    const url = `${API_BASE}/api/v1/reels/projects/${id}`;
    const headers = getAuthHeaders();

    logRequest('PUT', url, headers, project);

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(project)
    });

    const data = await response.json().catch(() => ({}));

    logResponse('PUT', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update motion project');
    }

    return unwrapProject(data);
  },

  async deleteProject(id) {
    const url = `${API_BASE}/api/v1/reels/projects/${id}`;
    const headers = getAuthHeaders();

    logRequest('DELETE', url, headers, null);

    const response = await fetch(url, { method: 'DELETE', headers });
    const data = await response.json().catch(() => ({}));

    logResponse('DELETE', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete motion project');
    }

    return unwrapProject(data);
  },

  async duplicateProject(id, name) {
    const url = `${API_BASE}/api/v1/reels/projects/${id}/duplicate`;
    const headers = getAuthHeaders();
    const body = { name };

    logRequest('POST', url, headers, body);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));

    logResponse('POST', url, response.status, data);

    if (!response.ok) {
      throw new Error(data.message || 'Failed to duplicate motion project');
    }

    return unwrapProject(data);
  }
};

export default ReelsProjectService;
